import { getAccessToken, setAccessToken } from './tokenStore'
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 10000
const REFRESH_URL = `${API_BASE_URL}/api/v1/auth/refresh`

let refreshPromise: Promise<string> | null = null
let sessionExpiredDispatched = false
let failedRefresh = false

export async function refreshAccessToken(): Promise<string> {
  if (failedRefresh) throw new Error('Refresh previously failed')
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const response = await fetch(REFRESH_URL, {
      method: 'POST',
      credentials: 'include', // sends the HTTP-only refreshToken cookie
      // No Content-Type header and no body — the endpoint reads only the cookie.
      // Sending Content-Type: application/json with an empty body causes
      // Fastify's body parser to reject the request with HTTP 400.
    })

    if (!response.ok) {
      if (response.status >= 400 && response.status < 500) {
        failedRefresh = true
      }
      throw new Error(`Refresh failed with status ${response.status}`)
    }

    const body = await response.json()
    const token: string | undefined = body?.accessToken ?? body?.data?.accessToken
    if (!token) throw new Error('No access token in refresh response')

    setAccessToken(token)
    return token
  })()

  refreshPromise.finally(() => { refreshPromise = null })
  return refreshPromise
}

export function resetRefreshState() {
  refreshPromise = null
  failedRefresh = false
  sessionExpiredDispatched = false
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
})

export const internalApiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: API_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
})

export interface ApiResponse<T = unknown> {
  data: T
  message?: string
  status: number
}

export interface ApiError {
  message: string
  status: number
  code?: string
}

const addAuthInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAccessToken()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
      config.headers['X-Request-ID'] = crypto.randomUUID()
      return config
    },
    (error) => Promise.reject(error)
  )
}

addAuthInterceptor(apiClient)
addAuthInterceptor(internalApiClient)

const addResponseInterceptor = (client: AxiosInstance) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      if (error.response?.status === 401) {
        const requestUrl = originalRequest?.url ?? ''
        const isRefreshEndpoint =
          requestUrl === REFRESH_URL || requestUrl.includes('/auth/refresh')

        if (originalRequest._retry || failedRefresh || isRefreshEndpoint) {
          setAccessToken(null)
          if (!sessionExpiredDispatched) {
            sessionExpiredDispatched = true
            globalThis.dispatchEvent(new CustomEvent('auth:sessionExpired'))
          }
          throw error
        }

        originalRequest._retry = true

        try {
          const newToken = await refreshAccessToken()
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
          }
          return client(originalRequest)
        } catch {
          setAccessToken(null)
          if (!sessionExpiredDispatched) {
            sessionExpiredDispatched = true
            globalThis.dispatchEvent(new CustomEvent('auth:sessionExpired'))
          }
          throw error
        }
      }

      if (error.response?.status === 429) {
        console.warn('Rate limit exceeded. Please try again later.')
      }

      throw error
    }
  )
}

addResponseInterceptor(apiClient)
addResponseInterceptor(internalApiClient)

export const api = apiClient
export default apiClient
